import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay, applyFilterOverlayImage} from '@renderer/utils/lutUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import cogoToast from '@successtar/cogo-toast'
import { useTranslation } from 'react-i18next'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { getNextPageAfterCapture, shouldBakeFilterAtCapture } from '@renderer/utils/navigationUtils'
import FrameBasedCapture from '../view/FrameBasedCapture'

interface UserContext {
  user?: {
    email: string
  }
}

const StandardPhotophoriaCapture: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const { setSessionState, userPreferences, id, gallery, galleryKey, uploadSessionState } =
    useSessionStateStore()
  const { config: globalConfig, getFilter, filters, getFrame } = useConfigStore()
  const config = globalConfig?.pages.capture
  const [capturedImageURL, setCapturedImageURL] = useState<string | null>(null)
  const [prepCountdown, setPrepCountdown] = useState<string>('00:00')
  const uniquePhotoTypes = new Set(
    getFrame(userPreferences.frameId || '')
      ?.drawParams.images.map((image) => image.type)
      .filter((type) => type.startsWith('photo-'))
  )
  const uniquePhotoCount = uniquePhotoTypes.size
  const initialCaptureCountdown = config?.settings.captureCountdown || 3
  const [captureCountdown, setCaptureCountdown] = useState<number>(initialCaptureCountdown)
  const [preparing, setPreparing] = useState(userPreferences.retakeCount === 0)
  const [capturing, setCapturing] = useState(false)
  const [sourceRatio, setSourceRatio] = useState<number | null>(null)
  const [isCapturingWithShutter, setIsCapturingWithShutter] = useState(false)
  const [isCaptureError, setIsCaptureError] = useState(false)

  const prepCountdownHook = useCountdown(
    userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
  )
  const captureCountdownHook = useCountdown(config?.settings.captureCountdown || 3, { audioCue: true })

  const stopCaptureRef = useRef<boolean>(false)

  const defaultCaptureCount = 6
  const captureCount = Math.max(
    uniquePhotoCount,
    config?.settings.captureCount || defaultCaptureCount
  )
  const remainingCapturesRef = useRef<number>(
    userPreferences.retakeCount > 0 ? userPreferences.retakeCount : captureCount
  )
  // In applyAfterCapture mode the Filter page runs AFTER capture, so the guest has not
  // picked a filter yet: capture clean originals and let Filter bake the choice in.
  const captureFilterId = userPreferences.filterId || filters[0]?._id
  const selectedFilter =
    shouldBakeFilterAtCapture(globalConfig) && captureFilterId
      ? getFilter(captureFilterId)
      : undefined
  const { downloadLocal, cameraSettings } = useSettingsStore()
  const { fetchSubscriptionAndCredits } = useCreditsStore()

  // Helper function to normalize captureRatio - 'auto' means no cropping
  const getNormalizedCaptureRatio = (): number | undefined => {
    if (cameraSettings.captureRatio === 'auto') return undefined
    if (typeof cameraSettings.captureRatio === 'number') return cameraSettings.captureRatio
    return 4 / 3 // Default fallback
  }

  const displayRatio = userPreferences.framePhotoRatio || getNormalizedCaptureRatio() || 4 / 3
  const canvas = document.querySelector('canvas')
  const canvasContext = canvas?.getContext('bitmaprenderer')

  const useRecording = userPreferences.useRecording
  const recordingSpeed = globalConfig?.pages.capture.settings.recordingSpeed || 1
  const targetDuration = (globalConfig?.pages.capture.settings.captureCountdown || 3) * 1000
  const FPS = 30
  const totalFrames = Math.ceil((targetDuration * FPS) / 1000)

  // Initialize recorders after canvas is ready
  const sessionCanvasRecorderRef = useRef<Recorder | null>(null)
  const captureCanvasRecorderRef = useRef<Recorder | null>(null)

  const validateDimensions = (width: number, height: number): { width: number; height: number } => {
    const validatedWidth = Math.floor(width / 2) * 2
    const validatedHeight = Math.floor(height / 2) * 2
    return { width: validatedWidth, height: validatedHeight }
  }

  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  useEffect(() => {
    if (canvasContext && canvas && canvasDimensions) {
      const validatedDimensions = validateDimensions(canvas?.width || 720, canvas?.height || 720)

      if (userPreferences.recordingType === 'session_recording') {
        const newSessionRecorder = new Recorder(canvasContext as RenderingContext, {
          name: `dreambooth-${id}-session`,
          encoderOptions: {
            codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
            width: validatedDimensions.width,
            height: validatedDimensions.height,
            bitrate: 2000000
          },
          duration: Infinity,
          download: downloadLocal && userPreferences.recordingType === 'session_recording',
          frameRate: FPS
        })
        sessionCanvasRecorderRef.current = newSessionRecorder
      }

      if (
        userPreferences.recordingType === 'strip_live' ||
        userPreferences.recordingType === 'strip_story'
      ) {
        const newCaptureRecorder = new Recorder(canvasContext as RenderingContext, {
          name: `dreambooth-${id}-capture`,
          encoderOptions: {
            codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
            width: validatedDimensions.width,
            height: validatedDimensions.height,
            bitrate: 2000000
          },
          duration: Infinity,
          download:
            downloadLocal &&
            (userPreferences.recordingType === 'strip_live' ||
              userPreferences.recordingType === 'strip_story'),
          frameRate: FPS
        })
        captureCanvasRecorderRef.current = newCaptureRecorder
      }
    }

    return (): void => {
      sessionCanvasRecorderRef.current?.stop()
      captureCanvasRecorderRef.current?.stop()
    }
  }, [canvasContext, canvas, canvasDimensions])

  // Load image and get its ratio when capturedImageURL changes
  useEffect(() => {
    if (capturedImageURL) {
      const img = new Image()
      img.onload = (): void => {
        setSourceRatio(img.width / img.height)
      }
      img.src = capturedImageURL
    }
  }, [capturedImageURL])

  // Calculate image style based on source and display ratios
  const getImageStyle = (): React.CSSProperties => {
    if (!sourceRatio) return { visibility: 'hidden' as const }

    return {
      width: sourceRatio > displayRatio ? `${(sourceRatio / displayRatio) * 100}%` : '100%',
      height: sourceRatio > displayRatio ? '100%' : `${(displayRatio / sourceRatio) * 100}%`,
      objectFit: 'cover' as const,
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    }
  }

  // Container style maintains the display ratio
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '200px',
    aspectRatio: `${displayRatio}`,
    overflow: 'hidden'
  }

  useEffect(() => {
    const prepTime = userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
    const captureTime = config?.settings.captureCountdown || 3

    // Upload session state on mount
    uploadSessionState()

    // Set up prep countdown callbacks
    prepCountdownHook.setOnTick(() => {
      setPrepCountdown(prepCountdownHook.getFormattedRemainingSeconds())
    })

    prepCountdownHook.setOnComplete(() => {
      // Log prep countdown timeout
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'capture',
              type: 'interaction',
              action: 'timeout',
              details: {
                type: 'prep',
                duration: prepTime,
                isRetake: userPreferences.retakeCount > 0
              },
              timestamp: new Date()
            }
          ]
        }
      })
      startCaptureSession()
    })

    // Set up capture countdown callbacks
    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
      setCapturing(true)
    })

    captureCountdownHook.setOnComplete(() => {
      // Log capture countdown timeout
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'capture',
              type: 'interaction',
              action: 'timeout',
              details: {
                type: 'capture',
                duration: captureTime,
                remainingCaptures: remainingCapturesRef.current,
                isRetake: userPreferences.retakeCount > 0
              },
              timestamp: new Date()
            }
          ]
        }
      })
      handleCapture()
    })

    // Handle auto-start when prepTimeout is null/0 (not a retake)
    const prepTimeout = config?.settings.prepTimeout
    const isRetake = userPreferences.retakeCount > 0
    if ((prepTimeout === null || prepTimeout === 0 || prepTimeout === undefined) && !isRetake) {
      // Auto-start immediately - this will trigger credit deduction in startCaptureSession()
      startCaptureSession()
    } else {
      prepCountdownHook.start()
    }

    return (): void => {
      sessionCanvasRecorderRef.current?.stop()
      captureCanvasRecorderRef.current?.stop()
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      stopCaptureRef.current = true // Signal the runCaptureLoop to stop
    }
  }, [canvasDimensions])

  const startCaptureSession = async (): Promise<void> => {
    stopCaptureRef.current = false // Reset the stopCaptureRef
    
    // Deduct credits only on first capture (not retakes)
    const isFirstCapture = userPreferences.retakeCount === 0
    if (isFirstCapture) {
      // Try to fetch subscription/credits to refresh auth tokens, but don't block session start
      // If offline or fetch fails, proceed anyway - session will queue for upload when online
      const isOnline = navigator.onLine
      if (isOnline) {
        // Online - try to fetch but don't block if it fails
        try {
          await Promise.race([
            fetchSubscriptionAndCredits(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
          ])
        } catch (error) {
          // If fetch fails or times out, log but don't block session start
          console.warn('[CAPTURE] Failed to fetch subscription/credits, proceeding anyway:', error)
        }
      } else {
        // Offline - start fetchSubscriptionAndCredits in background but don't wait
        fetchSubscriptionAndCredits().catch((error) => {
          console.warn('[CAPTURE] Background fetchSubscriptionAndCredits failed:', error)
        })
      }

      // Upload session state to trigger backend credit deduction
      // Backend will check if session exists, if user has subscription, etc.
      setTimeout(() => {
        uploadSessionState()
      }, 2000)
    }

    // Log processing start
    setSessionState({
      timeline: {
        activityLogs: [
          {
            page: 'capture',
            type: 'processing',
            action: 'start',
            details: {
              isRetake: userPreferences.retakeCount > 0,
              useRecording: useRecording
            },
            timestamp: new Date()
          }
        ]
      }
    })

    try {
      prepCountdownHook.stop()
      setPreparing(false)
      setCapturing(true)

      // Start session recording if enabled
      if (useRecording && sessionCanvasRecorderRef.current) {
        try {
          await sessionCanvasRecorderRef.current.start()
        } catch (error) {
          cogoToast.error('Error starting video recorder')
          console.error('Error starting session recorder', error)
          // Log recording failure
          setSessionState({
            timeline: {
              activityLogs: [
                {
                  page: 'capture',
                  type: 'processing',
                  action: 'failure',
                  details: {
                    error:
                      error instanceof Error
                        ? error.message
                        : t(
                            'capture.errorStartingSessionRecorder',
                            'Error starting session recorder'
                          ),
                    component: 'session_recorder'
                  },
                  timestamp: new Date()
                }
              ]
            }
          })
        }
      }

      // Log processing complete
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'capture',
              type: 'processing',
              action: 'complete',
              details: {
                isRetake: userPreferences.retakeCount > 0,
                useRecording: useRecording
              },
              timestamp: new Date()
            }
          ]
        }
      })

      runCaptureLoop()
    } catch (error) {
      console.error('Error in startCaptureSession:', error)
      // Log processing failure
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'capture',
              type: 'processing',
              action: 'failure',
              details: {
                error: error instanceof Error ? error.message : 'Unknown error',
                isRetake: userPreferences.retakeCount > 0
              },
              timestamp: new Date()
            }
          ]
        }
      })
    }
  }

  const stopRecording = async (): Promise<void> => {
    if (useRecording && sessionCanvasRecorderRef.current) {
      const recordedChunks = await sessionCanvasRecorderRef.current.stop()

      const mp4Blob = new Blob([recordedChunks as ArrayBuffer], { type: 'video/mp4' })
      const mp4Url = URL.createObjectURL(mp4Blob)

      const videoElement = document.createElement('video')
      videoElement.src = mp4Url
      videoElement.controls = true

      const videoFile = new File([mp4Blob], `dreambooth-${id}-session.mp4`, { type: 'video/mp4' })

      // Start video processing in background
      if (userPreferences.recordingType === 'session_recording') {
        backgroundVideoProcessor.processVideo({
          id: `session-${id}-${Date.now()}`,
          type: 'session',
          resources: [], // Not needed for capture videos
          frameConfig: {
            id: 'capture-video',
            baseImage: '',
            drawParams: {
              canvasWidth: canvasDimensions?.width || 1200,
              canvasHeight: canvasDimensions?.height || 1800,
              backgroundColor: '#FFFFFF',
              images: []
            }
          },
          sessionId: id || '',
          userId: context?.user?.email || '',
          projectId: projectId || '',
          galleryId: galleryKey || gallery.id,
          videoFile, // Pass the video file for capture videos
          onComplete: (url) => {
            // Update state with video URL when complete
            const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
            setSessionState({
              gallery: {
                capturedItems: [
                  ...prevItems,
                  {
                    id: 'dreambooth-session-recording-' + new Date().getTime(),
                    recordingUrl: mp4Url
                  }
                ]
              },
              timeline: {
                activityLogs: [
                  {
                    page: 'capture',
                    type: 'output' as const,
                    action: 'upload' as const,
                    details: {
                      type: 'recording',
                      url
                    },
                    timestamp: new Date()
                  }
                ]
              }
            })
          },
          onError: (error) => {
            console.error('Error processing session video:', error)
            cogoToast.error(
              t('capture.errorProcessingSessionVideo', 'Error processing session video')
            )
          }
        })
      }
    }
  }

  const runCaptureLoop = async (): Promise<void> => {
    console.log('Starting capture loop with remaining captures:', remainingCapturesRef.current)
    while (remainingCapturesRef.current > 0 && !stopCaptureRef.current) {
      let frameCount = 0
      const startTime = performance.now()
      const captureId = id + '-' + Date.now()
      let imageUrl: string | undefined = undefined
      let captureUrl: string | undefined = undefined

      console.log('Starting new capture iteration:', {
        remainingCaptures: remainingCapturesRef.current,
        useRecording,
        hasRecorder: !!captureCanvasRecorderRef.current
      })

      // Start capture recording if enabled
      if (useRecording && captureCanvasRecorderRef.current) {
        try {
          await captureCanvasRecorderRef.current.start()
          console.log('Successfully started capture recorder')
        } catch (error) {
          cogoToast.error('Error starting video recorder')
          console.error('Error starting capture recorder', error)
        }
      }

      // Start the countdown and wait for capture
      console.log('Attempting to start countdown')
      // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
      unifiedCameraUtils.prefocus()
      captureCountdownHook.start()

      const stepInterval = 1000 / (FPS / recordingSpeed)
      let sessionStepLoop: NodeJS.Timeout | null = null

      // Start session recording step loop if enabled
      if (useRecording && sessionCanvasRecorderRef.current) {
        sessionStepLoop = setInterval(async () => {
          if (sessionCanvasRecorderRef.current?.status !== RecorderStatus.Recording) return
          try {
            await sessionCanvasRecorderRef.current?.step()
          } catch (error) {
            console.error('Error stepping session recorder', error)
          }
        }, stepInterval)
      }

      // Create a promise for capture completion
      const captureCompletionPromise = new Promise<void>((resolve) => {
        captureCountdownHook.setOnComplete(() => {
          handleCapture().then((url) => {
            imageUrl = url
            resolve()
          })
        })
      })

      // Record frames during countdown if recording is enabled
      if (useRecording && captureCanvasRecorderRef.current) {
        while (frameCount < totalFrames) {
          if (captureCanvasRecorderRef.current.status !== RecorderStatus.Recording) break

          const currentTime = performance.now()
          const elapsedTime = currentTime - startTime
          const targetFrameTime = (frameCount * 1000) / FPS

          if (elapsedTime >= targetFrameTime) {
            try {
              await captureCanvasRecorderRef.current.step()
              frameCount++
            } catch (error) {
              console.error('Error stepping capture recorder', error)
            }
          }

          // Small delay to prevent CPU overload
          await new Promise((resolve) => setTimeout(resolve, 1))
        }
      }

      // Wait for capture to complete
      await captureCompletionPromise
      if (sessionStepLoop) {
        clearInterval(sessionStepLoop)
      }

      // Stop and save capture recording if enabled
      if (useRecording && captureCanvasRecorderRef.current) {
        try {
          const captureChunks = await captureCanvasRecorderRef.current.stop()
          const captureBlob = new Blob([captureChunks as ArrayBuffer], { type: 'video/mp4' })
          captureUrl = URL.createObjectURL(captureBlob)
        } catch (error) {
          console.error('Error stopping capture recorder', error)
        }
      }

      const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
      setSessionState({
        gallery: {
          capturedItems: [
            ...prevItems,
            {
              id: captureId,
              photoUrl: imageUrl || '',
              recordingUrl: captureUrl || '',
              timestamp: new Date()
            }
          ]
        },
        timeline: {
          activityLogs: [
            {
              page: 'capture',
              type: 'output',
              action: 'capture',
              details: {
                id: captureId,
                hasPhoto: !!imageUrl,
                hasRecording: !!captureUrl,
                remainingCaptures: remainingCapturesRef.current,
                isRetake: userPreferences.retakeCount > 0
              },
              timestamp: new Date()
            }
          ]
        }
      })

      // Reset countdown for next capture
      if (remainingCapturesRef.current > 1) {
        console.log('Resetting countdown for next capture')
        const captureTime = config?.settings.captureCountdown || 3
        captureCountdownHook.setRemainingSeconds(captureTime)
      } else {
        console.log('Not resetting countdown - no more captures remaining')
      }
    }

    // Stop and save session recording if enabled
    if (useRecording && sessionCanvasRecorderRef.current) {
      try {
        await stopRecording()
      } catch (error) {
        console.error('Error stopping session recording', error)
      }
    }

    // Only navigate if the session was not cancelled
    if (stopCaptureRef.current) return

    // Upload session state before navigating
    uploadSessionState()

    // Check if retakes are still allowed
    const maxRetakeSession = globalConfig?.pages.retake?.settings?.maxRetakeSession
    const retakeSessionCount = userPreferences.retakeSessionsCompleted || 0

    const canRetake =
      maxRetakeSession === undefined ||
      maxRetakeSession === null ||
      retakeSessionCount < maxRetakeSession

    navigate(`/${projectId}/${getNextPageAfterCapture(globalConfig, canRetake)}`)
  }

  const handleCapture = async (): Promise<string | undefined> => {
    try {
      console.log('Starting capture process')
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'capture',
              type: 'processing',
              action: 'start',
              details: {
                remainingCaptures: remainingCapturesRef.current,
                isRetake: userPreferences.retakeCount > 0
              },
              timestamp: new Date()
            }
          ]
        }
      })

      flashEffect('.flash')
      setCapturing(false)
      setIsCaptureError(false)

      console.log('Capture settings:', {
        useShutterCapture: cameraSettings.useShutterCapture,
        deviceType: useCameraStore.getState().deviceType,
        captureRatio: cameraSettings.captureRatio,
        mirror: cameraSettings.mirror
      })

      if (cameraSettings.useShutterCapture && useCameraStore.getState().deviceType == 'camera') {
        setIsCapturingWithShutter(true)
      }

      await new Promise((resolve) => setTimeout(resolve, 200))
      console.log('Attempting to capture image')
      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        applyFilter,
        getNormalizedCaptureRatio(),
        !cameraSettings.useShutterCapture
      )

      setIsCapturingWithShutter(false)

      if (imageData) {
        console.log('Successfully captured image')
        const imageUrl = URL.createObjectURL(imageData)
        setCapturedImageURL(imageUrl)

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.text = 'Download photo'
          a.download = `dreambooth-${id}-${Date.now()}.jpg`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)

          setSessionState({
            timeline: {
              activityLogs: [
                {
                  page: 'capture',
                  type: 'output',
                  action: 'download',
                  details: {
                    type: 'photo',
                    filename: `dreambooth-${id}-${Date.now()}.jpg`
                  },
                  timestamp: new Date()
                }
              ]
            }
          })
        }

        remainingCapturesRef.current--

        setSessionState({
          timeline: {
            activityLogs: [
              {
                page: 'capture',
                type: 'processing',
                action: 'complete',
                details: {
                  remainingCaptures: remainingCapturesRef.current,
                  isRetake: userPreferences.retakeCount > 0
                },
                timestamp: new Date()
              }
            ]
          }
        })

        return imageUrl
      }

      // Log processing failure if no image data
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'capture',
              type: 'processing',
              action: 'failure',
              details: {
                error: 'No image data captured',
                remainingCaptures: remainingCapturesRef.current,
                isRetake: userPreferences.retakeCount > 0
              },
              timestamp: new Date()
            }
          ]
        }
      })

      setIsCaptureError(true)
      return undefined
    } catch (error) {
      console.error('Capture failed:', error)
      setCapturing(false)
      setIsCapturingWithShutter(false)
      setIsCaptureError(true)

      // Log processing failure
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'capture',
              type: 'processing',
              action: 'failure',
              details: {
                error: error instanceof Error ? error.message : 'Unknown error',
                remainingCaptures: remainingCapturesRef.current,
                isRetake: userPreferences.retakeCount > 0
              },
              timestamp: new Date()
            }
          ]
        }
      })

      return undefined
    }
  }

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (selectedFilter?.adjustments) {
      filterUtils.applyAdjustments(ctx, selectedFilter.adjustments)
      applyFilterOverlayImage(ctx, selectedFilter?.overlay)
    }
  }

  // Display progress
  const captureProgress = {
    current:
      userPreferences.retakeCount > 0
        ? userPreferences.retakeCount - remainingCapturesRef.current
        : captureCount - remainingCapturesRef.current,
    total: userPreferences.retakeCount > 0 ? userPreferences.retakeCount : captureCount
  }

  // Cache images for better performance
  const cachedCaptureBg = useCachedImage('/assets/photophoria/photophoria-capture.webp')
  const cachedCaptureTitle = useCachedImage('/assets/photophoria/photophoria-capture-title.webp')
  const cachedCaptureButton = useCachedImage('/assets/photophoria/photophoria-capture-button.webp')

  return (
    <div className="relative w-full h-full" data-page="capture">
      <div id="flash" className="w-full h-full flash"></div>

      {/* The main preview component, behind the frame */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full z-20">
        <Preview
          key={`${selectedFilter}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${getNormalizedCaptureRatio()}-${displayRatio}`}
          adjustments={selectedFilter?.adjustments}
          filter={selectedFilter?._id}
          postLUTAdjustments={selectedFilter?.postLUTAdjustments}
          ratio={displayRatio}
          onCanvasReady={setCanvasDimensions}
        />
      </div>

      {/* Overlay for background frame, on top of preview */}
      <img
        src={cachedCaptureBg}
        alt="Background Frame"
        className="absolute inset-0 w-full h-full object-cover z-40"
      />

      {/* Shutter capture message (highest z-index) */}
      {isCapturingWithShutter && (
        <div className="absolute inset-0 bg-white/50 z-[60] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-black mb-4">
              📸 {t('capture.lookAtCamera', 'Look at the Camera')}
            </h2>
            <p className="text-xl text-black/80">
              {t('capture.stayStill', 'Please stay still while we capture your photo...')}
            </p>
          </div>
        </div>
      )}

      {/* Capture error message (highest z-index) */}
      {isCaptureError && (
        <div className="absolute inset-0 bg-black/80 z-[60] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-red-500 mb-4">
              {t('capture.captureFailed', 'Capture Failed')}
            </h2>
            <p className="text-xl text-white/80 mb-6">
              {t('capture.stayStillCapturing', 'Please stay still when capturing')}
            </p>
            <p className="text-lg text-white/60">
              {t('capture.retryingMoment', 'Retrying in a moment...')}
            </p>
          </div>
        </div>
      )}

      {/* Captured image preview (behind frame) */}
      <div className="absolute bottom-24 right-64 flex flex-col items-center gap-4 z-30">
        <div className="bg-white/10 p-2 rounded-lg">
          {capturedImageURL ? (
            <div style={containerStyle}>
              <img
                src={capturedImageURL}
                alt={t('capture.lastCaptured', 'Last captured')}
                style={getImageStyle()}
                crossOrigin="anonymous"
              />
            </div>
          ) : (
            <div
              style={{
                width: 200 * displayRatio,
                height: 200
              }}
              className="flex items-center justify-center bg-black/30"
            >
              <span className="text-white/50 text-xl">
                {t('capture.noPhotoYet', 'No photo yet')}
              </span>
            </div>
          )}
        </div>
        <span className="font-bold text-white text-5xl">
          {captureProgress.current}/{captureProgress.total}
        </span>
      </div>

      {/* Preparing and Capturing overlays (on top of frame) */}
      {preparing && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-50 items-center justify-center">
          <div className="flex flex-col items-center max-w-4xl text-center px-8">
            {/* Replaced 'Get Ready!' text with image */}
            <img
              src={cachedCaptureTitle}
              alt={t('capture.getReady', 'Get Ready!')}
              className="w-[1000px] h-auto"
            />

            <p className="text-3xl text-white/80">
              {t('capture.sessionStarting', 'Session will start in')}{' '}
              <span className="text-4xl font-bold text-white">{prepCountdown}</span>
            </p>

            <div className="flex flex-col items-center gap-3 mt-24">
              {/* Replaced 'Start Now' button with image button */}
              <button
                onClick={startCaptureSession}
                className={`transition-transform transform hover:scale-105 ${!canvasDimensions ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!canvasDimensions}
              >
                <img
                  src={cachedCaptureButton}
                  alt={t('capture.startNow', 'Start Now')}
                  className="w-[400px] h-auto"
                />
              </button>
              <span className="text-white/60 text-lg">
                {t('capture.orWaitCountdown', 'or wait for the countdown')}
              </span>
            </div>
          </div>
        </div>
      )}

      {capturing && (
        <div className="absolute inset-0 z-50 flex items-center justify-center">
          <span
            key={captureCountdown}
            className="text-[348px] text-white animate-countdown drop-shadow-lg"
          >
            {captureCountdown}
          </span>
        </div>
      )}

      {/* Recording indicator */}
      {userPreferences.useRecording && !preparing && (
        <div className="absolute top-12 left-12 z-55 flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-500 text-[24px]">{t('capture.recording', 'REC')}</span>
          </div>
        </div>
      )}
    </div>
  )
}

const CapturePage: React.FC = () => {
  const { config: globalConfig } = useConfigStore()
  const captureMode = globalConfig?.pages.capture?.settings?.mode || 'standard'
  if (captureMode === 'frame-based') {
    return <FrameBasedCapture />
  }
  return <StandardPhotophoriaCapture />
}

export default CapturePage
